import { Point, FloorPlanElement } from '../types';
import { PIXELS_PER_METER } from '../constants/floorPlanConstants';

export type FireSafetyType = 
  | 'emergency-light' 
  | 'fire-call-point' 
  | 'fire-panel'
  | 'heat-alarm'
  | 'alarm-sounder'
  | 'wc-call-point'
  | 'ac-unit'
  | 'fire-alarm'
  | 'ice-machine'
  | 'coffee-machine';

export interface FireSafetyElement {
  id: string;
  type: FireSafetyType;
  position: Point;
  floor: number;
}

export function createFireSafetyElement(point: Point, elementType: FireSafetyType, floor: number): FireSafetyElement {
  return {
    id: `${elementType}-preview-${Date.now()}`,
    type: elementType,
    position: point,
    floor,
  };
}

// Draw emergency light icon - light bulb with rays and running man
function drawEmergencyLightIcon(ctx: CanvasRenderingContext2D, center: Point, size: number) {
  const radius = size / 2;
  
  // Draw outer circle
  ctx.beginPath();
  ctx.arc(center.x, center.y, radius, 0, 2 * Math.PI);
  ctx.fill();
  ctx.stroke();
  
  // Draw light rays (4 corner rays)
  const originalLineWidth = ctx.lineWidth;
  ctx.lineWidth = originalLineWidth * 1.5;
  const rayAngles = [Math.PI / 4, 3 * Math.PI / 4, 5 * Math.PI / 4, 7 * Math.PI / 4];
  rayAngles.forEach(angle => {
    ctx.beginPath();
    const startX = center.x + Math.cos(angle) * radius * 0.9;
    const startY = center.y + Math.sin(angle) * radius * 0.9;
    const endX = center.x + Math.cos(angle) * radius * 1.6;
    const endY = center.y + Math.sin(angle) * radius * 1.6;
    ctx.moveTo(startX, startY);
    ctx.lineTo(endX, endY);
    ctx.stroke();
  });
  ctx.lineWidth = originalLineWidth;
  
  // Draw white "E" in center
  ctx.save();
  ctx.fillStyle = '#ffffff';
  const fontSize = size * 0.5;
  ctx.font = `bold ${fontSize}px Arial`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('E', center.x, center.y);
  ctx.restore();
}

// Draw fire call point icon - square with bell/alarm symbol
function drawFireCallPointIcon(ctx: CanvasRenderingContext2D, center: Point, size: number) {
  const halfSize = size / 2;
  
  // Draw outer square with rounded corners
  const cornerRadius = size * 0.15;
  ctx.beginPath();
  ctx.moveTo(center.x - halfSize + cornerRadius, center.y - halfSize);
  ctx.lineTo(center.x + halfSize - cornerRadius, center.y - halfSize);
  ctx.arcTo(center.x + halfSize, center.y - halfSize, center.x + halfSize, center.y - halfSize + cornerRadius, cornerRadius);
  ctx.lineTo(center.x + halfSize, center.y + halfSize - cornerRadius);
  ctx.arcTo(center.x + halfSize, center.y + halfSize, center.x + halfSize - cornerRadius, center.y + halfSize, cornerRadius);
  ctx.lineTo(center.x - halfSize + cornerRadius, center.y + halfSize);
  ctx.arcTo(center.x - halfSize, center.y + halfSize, center.x - halfSize, center.y + halfSize - cornerRadius, cornerRadius);
  ctx.lineTo(center.x - halfSize, center.y - halfSize + cornerRadius);
  ctx.arcTo(center.x - halfSize, center.y - halfSize, center.x - halfSize + cornerRadius, center.y - halfSize, cornerRadius);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  
  // Draw white bell/alarm icon
  ctx.save();
  ctx.strokeStyle = '#ffffff';
  ctx.fillStyle = '#ffffff';
  const bellSize = size * 0.35;
  
  // Bell body (triangle/trapezoid)
  ctx.beginPath();
  ctx.moveTo(center.x, center.y - bellSize * 0.5);
  ctx.lineTo(center.x - bellSize * 0.6, center.y + bellSize * 0.3);
  ctx.lineTo(center.x + bellSize * 0.6, center.y + bellSize * 0.3);
  ctx.closePath();
  ctx.fill();
  
  // Bell clapper (small circle)
  ctx.beginPath();
  ctx.arc(center.x, center.y + bellSize * 0.5, bellSize * 0.15, 0, 2 * Math.PI);
  ctx.fill();
  
  ctx.restore();
}

// Draw fire panel icon - rectangular panel with LED display
function drawFirePanelIcon(ctx: CanvasRenderingContext2D, center: Point, size: number) {
  const width = size * 1.3;
  const height = size * 0.9;
  
  // Draw panel rectangle with rounded corners
  const cornerRadius = size * 0.1;
  ctx.beginPath();
  ctx.moveTo(center.x - width / 2 + cornerRadius, center.y - height / 2);
  ctx.lineTo(center.x + width / 2 - cornerRadius, center.y - height / 2);
  ctx.arcTo(center.x + width / 2, center.y - height / 2, center.x + width / 2, center.y - height / 2 + cornerRadius, cornerRadius);
  ctx.lineTo(center.x + width / 2, center.y + height / 2 - cornerRadius);
  ctx.arcTo(center.x + width / 2, center.y + height / 2, center.x + width / 2 - cornerRadius, center.y + height / 2, cornerRadius);
  ctx.lineTo(center.x - width / 2 + cornerRadius, center.y + height / 2);
  ctx.arcTo(center.x - width / 2, center.y + height / 2, center.x - width / 2, center.y + height / 2 - cornerRadius, cornerRadius);
  ctx.lineTo(center.x - width / 2, center.y - height / 2 + cornerRadius);
  ctx.arcTo(center.x - width / 2, center.y - height / 2, center.x - width / 2 + cornerRadius, center.y - height / 2, cornerRadius);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  
  // Draw LCD display screen (darker rectangle)
  ctx.save();
  ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
  const screenWidth = width * 0.7;
  const screenHeight = height * 0.4;
  ctx.fillRect(center.x - screenWidth / 2, center.y - height * 0.25, screenWidth, screenHeight);
  
  // Draw status LEDs below screen
  ctx.fillStyle = '#ffffff';
  const ledRadius = size * 0.08;
  const ledY = center.y + height * 0.25;
  [-width * 0.25, 0, width * 0.25].forEach(x => {
    ctx.beginPath();
    ctx.arc(center.x + x, ledY, ledRadius, 0, 2 * Math.PI);
    ctx.fill();
  });
  
  ctx.restore();
}

// Draw heat alarm icon - circle with flame symbol
function drawHeatAlarmIcon(ctx: CanvasRenderingContext2D, center: Point, size: number) {
  const radius = size / 2;
  
  // Draw outer circle
  ctx.beginPath();
  ctx.arc(center.x, center.y, radius, 0, 2 * Math.PI);
  ctx.fill();
  ctx.stroke();
  
  // Draw flame icon in white
  ctx.save();
  ctx.fillStyle = '#ffffff';
  ctx.strokeStyle = '#ffffff';
  
  const flameSize = size * 0.45;
  const flameBase = center.y + flameSize * 0.4;
  const flameTip = center.y - flameSize * 0.4;
  
  // Main flame shape (teardrop)
  ctx.beginPath();
  ctx.moveTo(center.x, flameTip);
  // Right side curve
  ctx.bezierCurveTo(
    center.x + flameSize * 0.4, flameTip + flameSize * 0.3,
    center.x + flameSize * 0.35, flameBase - flameSize * 0.2,
    center.x, flameBase
  );
  // Left side curve
  ctx.bezierCurveTo(
    center.x - flameSize * 0.35, flameBase - flameSize * 0.2,
    center.x - flameSize * 0.4, flameTip + flameSize * 0.3,
    center.x, flameTip
  );
  ctx.fill();
  
  // Inner flame detail
  ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
  const innerFlameSize = flameSize * 0.5;
  ctx.beginPath();
  ctx.moveTo(center.x, center.y - innerFlameSize * 0.3);
  ctx.bezierCurveTo(
    center.x + innerFlameSize * 0.3, center.y,
    center.x + innerFlameSize * 0.25, center.y + innerFlameSize * 0.2,
    center.x, center.y + innerFlameSize * 0.3
  );
  ctx.bezierCurveTo(
    center.x - innerFlameSize * 0.25, center.y + innerFlameSize * 0.2,
    center.x - innerFlameSize * 0.3, center.y,
    center.x, center.y - innerFlameSize * 0.3
  );
  ctx.fill();
  
  ctx.restore();
}

// Draw alarm sounder icon - speaker with sound waves
function drawAlarmSounderIcon(ctx: CanvasRenderingContext2D, center: Point, size: number) {
  const halfSize = size / 2;
  
  // Draw speaker body (rectangle)
  ctx.beginPath();
  ctx.rect(center.x - halfSize * 0.7, center.y - halfSize * 0.6, halfSize * 0.5, halfSize * 1.2);
  ctx.fill();
  ctx.stroke();
  
  // Draw speaker cone (trapezoid)
  ctx.beginPath();
  ctx.moveTo(center.x - halfSize * 0.2, center.y - halfSize * 0.5);
  ctx.lineTo(center.x + halfSize * 0.5, center.y - halfSize * 0.8);
  ctx.lineTo(center.x + halfSize * 0.5, center.y + halfSize * 0.8);
  ctx.lineTo(center.x - halfSize * 0.2, center.y + halfSize * 0.5);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  
  // Draw sound waves (curved arcs)
  const originalLineWidth = ctx.lineWidth;
  ctx.lineWidth = originalLineWidth * 1.2;
  const waveStart = center.x + halfSize * 0.6;
  
  for (let i = 1; i <= 3; i++) {
    const waveRadius = halfSize * 0.3 * i;
    ctx.beginPath();
    ctx.arc(waveStart, center.y, waveRadius, -Math.PI / 3, Math.PI / 3);
    ctx.stroke();
  }
  ctx.lineWidth = originalLineWidth;
}

// Draw W/C call point icon - circle with accessibility/assistance symbol
function drawWCCallPointIcon(ctx: CanvasRenderingContext2D, center: Point, size: number) {
  const radius = size / 2;
  
  // Draw outer circle
  ctx.beginPath();
  ctx.arc(center.x, center.y, radius, 0, 2 * Math.PI);
  ctx.fill();
  ctx.stroke();
  
  // Draw white medical/assistance cross inside
  ctx.save();
  ctx.fillStyle = '#ffffff';
  const crossSize = radius * 0.55;
  const crossThickness = crossSize * 0.35;
  
  // Vertical bar of cross
  ctx.fillRect(
    center.x - crossThickness / 2,
    center.y - crossSize,
    crossThickness,
    crossSize * 2
  );
  
  // Horizontal bar of cross
  ctx.fillRect(
    center.x - crossSize,
    center.y - crossThickness / 2,
    crossSize * 2,
    crossThickness
  );
  
  ctx.restore();
}

// Draw AC unit icon - rectangular unit with fan and vents
function drawACUnitIcon(ctx: CanvasRenderingContext2D, center: Point, size: number) {
  const width = size * 1.4;
  const height = size * 0.8;
  
  // Draw unit box with rounded corners
  const cornerRadius = size * 0.1;
  ctx.beginPath();
  ctx.moveTo(center.x - width / 2 + cornerRadius, center.y - height / 2);
  ctx.lineTo(center.x + width / 2 - cornerRadius, center.y - height / 2);
  ctx.arcTo(center.x + width / 2, center.y - height / 2, center.x + width / 2, center.y - height / 2 + cornerRadius, cornerRadius);
  ctx.lineTo(center.x + width / 2, center.y + height / 2 - cornerRadius);
  ctx.arcTo(center.x + width / 2, center.y + height / 2, center.x + width / 2 - cornerRadius, center.y + height / 2, cornerRadius);
  ctx.lineTo(center.x - width / 2 + cornerRadius, center.y + height / 2);
  ctx.arcTo(center.x - width / 2, center.y + height / 2, center.x - width / 2, center.y + height / 2 - cornerRadius, cornerRadius);
  ctx.lineTo(center.x - width / 2, center.y - height / 2 + cornerRadius);
  ctx.arcTo(center.x - width / 2, center.y - height / 2, center.x - width / 2 + cornerRadius, center.y - height / 2, cornerRadius);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  
  // Draw fan symbol (circle with 3 blades)
  ctx.save();
  ctx.strokeStyle = '#ffffff';
  ctx.fillStyle = '#ffffff';
  
  const fanRadius = height * 0.3;
  // Fan center circle
  ctx.beginPath();
  ctx.arc(center.x, center.y, fanRadius * 0.2, 0, 2 * Math.PI);
  ctx.fill();
  
  // Fan blades (3 curved arcs)
  const originalLineWidth = ctx.lineWidth;
  ctx.lineWidth = originalLineWidth * 1.5;
  for (let i = 0; i < 3; i++) {
    const angle = (i * 2 * Math.PI) / 3;
    ctx.beginPath();
    ctx.arc(
      center.x + Math.cos(angle) * fanRadius * 0.5,
      center.y + Math.sin(angle) * fanRadius * 0.5,
      fanRadius * 0.6,
      angle + Math.PI / 2,
      angle + Math.PI
    );
    ctx.stroke();
  }
  ctx.lineWidth = originalLineWidth;
  
  ctx.restore();
}

// Draw fire alarm icon - circle with radiating lines and bell
function drawFireAlarmIcon(ctx: CanvasRenderingContext2D, center: Point, size: number) {
  const radius = size / 2;
  
  // Draw outer circle
  ctx.beginPath();
  ctx.arc(center.x, center.y, radius, 0, 2 * Math.PI);
  ctx.fill();
  ctx.stroke();
  
  // Draw alert lines radiating outward (4 corners)
  const originalLineWidth = ctx.lineWidth;
  ctx.lineWidth = originalLineWidth * 1.5;
  const alertAngles = [0, Math.PI / 2, Math.PI, 3 * Math.PI / 2];
  alertAngles.forEach(angle => {
    ctx.beginPath();
    const x1 = center.x + Math.cos(angle) * radius * 0.85;
    const y1 = center.y + Math.sin(angle) * radius * 0.85;
    const x2 = center.x + Math.cos(angle) * radius * 1.5;
    const y2 = center.y + Math.sin(angle) * radius * 1.5;
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  });
  ctx.lineWidth = originalLineWidth;
  
  // Draw white bell icon in center
  ctx.save();
  ctx.fillStyle = '#ffffff';
  ctx.strokeStyle = '#ffffff';
  
  const bellSize = size * 0.3;
  // Bell body
  ctx.beginPath();
  ctx.moveTo(center.x, center.y - bellSize * 0.4);
  ctx.lineTo(center.x - bellSize * 0.5, center.y + bellSize * 0.2);
  ctx.lineTo(center.x + bellSize * 0.5, center.y + bellSize * 0.2);
  ctx.closePath();
  ctx.fill();
  
  // Bell clapper
  ctx.beginPath();
  ctx.arc(center.x, center.y + bellSize * 0.4, bellSize * 0.12, 0, 2 * Math.PI);
  ctx.fill();
  
  ctx.restore();
}

// Draw ice machine icon - rectangular appliance with ice cube symbol
function drawIceMachineIcon(ctx: CanvasRenderingContext2D, center: Point, size: number) {
  const width = size * 1.2;
  const height = size;
  
  // Draw appliance body with rounded corners
  const cornerRadius = size * 0.12;
  ctx.beginPath();
  ctx.moveTo(center.x - width / 2 + cornerRadius, center.y - height / 2);
  ctx.lineTo(center.x + width / 2 - cornerRadius, center.y - height / 2);
  ctx.arcTo(center.x + width / 2, center.y - height / 2, center.x + width / 2, center.y - height / 2 + cornerRadius, cornerRadius);
  ctx.lineTo(center.x + width / 2, center.y + height / 2 - cornerRadius);
  ctx.arcTo(center.x + width / 2, center.y + height / 2, center.x + width / 2 - cornerRadius, center.y + height / 2, cornerRadius);
  ctx.lineTo(center.x - width / 2 + cornerRadius, center.y + height / 2);
  ctx.arcTo(center.x - width / 2, center.y + height / 2, center.x - width / 2, center.y + height / 2 - cornerRadius, cornerRadius);
  ctx.lineTo(center.x - width / 2, center.y - height / 2 + cornerRadius);
  ctx.arcTo(center.x - width / 2, center.y - height / 2, center.x - width / 2 + cornerRadius, center.y - height / 2, cornerRadius);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  
  // Draw ice cube symbols (3 small squares)
  ctx.save();
  ctx.fillStyle = '#ffffff';
  ctx.strokeStyle = '#ffffff';
  
  const cubeSize = size * 0.2;
  const positions = [
    { x: center.x - cubeSize * 0.8, y: center.y - cubeSize * 0.5 },
    { x: center.x + cubeSize * 0.8, y: center.y - cubeSize * 0.5 },
    { x: center.x, y: center.y + cubeSize * 0.6 }
  ];
  
  positions.forEach(pos => {
    ctx.beginPath();
    ctx.rect(pos.x - cubeSize / 2, pos.y - cubeSize / 2, cubeSize, cubeSize);
    ctx.fill();
    ctx.stroke();
  });
  
  ctx.restore();
}

// Draw coffee machine icon - rectangular appliance with cup symbol
function drawCoffeeMachineIcon(ctx: CanvasRenderingContext2D, center: Point, size: number) {
  const width = size * 1.1;
  const height = size * 1.1;
  
  // Draw appliance body with rounded corners
  const cornerRadius = size * 0.1;
  ctx.beginPath();
  ctx.moveTo(center.x - width / 2 + cornerRadius, center.y - height / 2);
  ctx.lineTo(center.x + width / 2 - cornerRadius, center.y - height / 2);
  ctx.arcTo(center.x + width / 2, center.y - height / 2, center.x + width / 2, center.y - height / 2 + cornerRadius, cornerRadius);
  ctx.lineTo(center.x + width / 2, center.y + height / 2 - cornerRadius);
  ctx.arcTo(center.x + width / 2, center.y + height / 2, center.x + width / 2 - cornerRadius, center.y + height / 2, cornerRadius);
  ctx.lineTo(center.x - width / 2 + cornerRadius, center.y + height / 2);
  ctx.arcTo(center.x - width / 2, center.y + height / 2, center.x - width / 2, center.y + height / 2 - cornerRadius, cornerRadius);
  ctx.lineTo(center.x - width / 2, center.y - height / 2 + cornerRadius);
  ctx.arcTo(center.x - width / 2, center.y - height / 2, center.x - width / 2 + cornerRadius, center.y - height / 2, cornerRadius);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  
  // Draw coffee cup icon in white
  ctx.save();
  ctx.fillStyle = '#ffffff';
  ctx.strokeStyle = '#ffffff';
  
  const cupSize = size * 0.4;
  const cupTop = center.y - cupSize * 0.3;
  const cupBottom = center.y + cupSize * 0.5;
  
  // Cup body (trapezoid)
  ctx.beginPath();
  ctx.moveTo(center.x - cupSize * 0.4, cupTop);
  ctx.lineTo(center.x + cupSize * 0.4, cupTop);
  ctx.lineTo(center.x + cupSize * 0.5, cupBottom);
  ctx.lineTo(center.x - cupSize * 0.5, cupBottom);
  ctx.closePath();
  ctx.fill();
  
  // Cup handle (arc)
  ctx.lineWidth = ctx.lineWidth * 1.5;
  ctx.beginPath();
  ctx.arc(center.x + cupSize * 0.5, center.y + cupSize * 0.1, cupSize * 0.25, -Math.PI / 4, Math.PI / 2);
  ctx.stroke();
  
  // Steam lines above cup
  ctx.lineWidth = ctx.lineWidth * 0.8;
  const steamY = cupTop - cupSize * 0.3;
  [-cupSize * 0.2, 0, cupSize * 0.2].forEach(xOffset => {
    ctx.beginPath();
    ctx.moveTo(center.x + xOffset, cupTop - cupSize * 0.1);
    ctx.quadraticCurveTo(
      center.x + xOffset + cupSize * 0.1, steamY - cupSize * 0.1,
      center.x + xOffset, steamY
    );
    ctx.stroke();
  });
  
  ctx.restore();
}

export function drawFireSafetyElements(
  ctx: CanvasRenderingContext2D,
  elements: FloorPlanElement[],
  currentFloor: number,
  scale: number,
  viewOffset: Point,
  isEstate: boolean = false
) {
  const fireSafetyElements = elements.filter(
    (el): el is FireSafetyElement => 
      (el.type === 'emergency-light' || el.type === 'fire-call-point' || el.type === 'fire-panel' ||
       el.type === 'heat-alarm' || el.type === 'alarm-sounder' || el.type === 'wc-call-point' || el.type === 'ac-unit' ||
       el.type === 'fire-alarm' || el.type === 'ice-machine' || el.type === 'coffee-machine') && 
      el.floor === currentFloor
  );

  if (fireSafetyElements.length === 0) return;

  ctx.save();
  ctx.lineWidth = 2 / scale;

  fireSafetyElements.forEach(element => {
    const size = (0.25 * PIXELS_PER_METER) / scale;
    
    // 🏢 ESTATE MODE: Use black for all fire safety elements
    const color = isEstate ? '#000000' : undefined;
    
    if (element.type === 'emergency-light') {
      ctx.fillStyle = color || '#10b981';
      ctx.strokeStyle = color || '#10b981';
      drawEmergencyLightIcon(ctx, element.position, size);
    } else if (element.type === 'fire-call-point') {
      ctx.fillStyle = color || '#dc2626';
      ctx.strokeStyle = color || '#dc2626';
      drawFireCallPointIcon(ctx, element.position, size);
    } else if (element.type === 'fire-panel') {
      ctx.fillStyle = color || '#ea580c';
      ctx.strokeStyle = color || '#ea580c';
      drawFirePanelIcon(ctx, element.position, size);
    } else if (element.type === 'heat-alarm') {
      ctx.fillStyle = color || '#f59e0b';
      ctx.strokeStyle = color || '#f59e0b';
      drawHeatAlarmIcon(ctx, element.position, size);
    } else if (element.type === 'alarm-sounder') {
      ctx.fillStyle = color || '#8b5cf6';
      ctx.strokeStyle = color || '#8b5cf6';
      drawAlarmSounderIcon(ctx, element.position, size);
    } else if (element.type === 'wc-call-point') {
      ctx.fillStyle = color || '#3b82f6';
      ctx.strokeStyle = color || '#3b82f6';
      drawWCCallPointIcon(ctx, element.position, size);
    } else if (element.type === 'ac-unit') {
      ctx.fillStyle = color || '#06b6d4';
      ctx.strokeStyle = color || '#06b6d4';
      drawACUnitIcon(ctx, element.position, size);
    } else if (element.type === 'fire-alarm') {
      ctx.fillStyle = color || '#ef4444';
      ctx.strokeStyle = color || '#ef4444';
      drawFireAlarmIcon(ctx, element.position, size);
    } else if (element.type === 'ice-machine') {
      ctx.fillStyle = color || '#0ea5e9';
      ctx.strokeStyle = color || '#0ea5e9';
      drawIceMachineIcon(ctx, element.position, size);
    } else if (element.type === 'coffee-machine') {
      ctx.fillStyle = color || '#92400e';
      ctx.strokeStyle = color || '#92400e';
      drawCoffeeMachineIcon(ctx, element.position, size);
    }
  });

  ctx.restore();
}

export function drawFireSafetyPreview(
  ctx: CanvasRenderingContext2D,
  preview: FireSafetyElement | null,
  scale: number,
  viewOffset: Point
) {
  if (!preview) return;

  ctx.save();
  ctx.globalAlpha = 0.6;
  ctx.lineWidth = 2 / scale;

  const size = (0.25 * PIXELS_PER_METER) / scale;

  // Different colors and icons for different element types
  if (preview.type === 'emergency-light') {
    ctx.fillStyle = '#10b981'; // Green-500
    ctx.strokeStyle = '#10b981';
    drawEmergencyLightIcon(ctx, preview.position, size);
  } else if (preview.type === 'fire-call-point') {
    ctx.fillStyle = '#dc2626'; // Red-600
    ctx.strokeStyle = '#dc2626';
    drawFireCallPointIcon(ctx, preview.position, size);
  } else if (preview.type === 'fire-panel') {
    ctx.fillStyle = '#ea580c'; // Orange-600
    ctx.strokeStyle = '#ea580c';
    drawFirePanelIcon(ctx, preview.position, size);
  } else if (preview.type === 'heat-alarm') {
    ctx.fillStyle = '#f59e0b'; // Amber-500
    ctx.strokeStyle = '#f59e0b';
    drawHeatAlarmIcon(ctx, preview.position, size);
  } else if (preview.type === 'alarm-sounder') {
    ctx.fillStyle = '#8b5cf6'; // Violet-500
    ctx.strokeStyle = '#8b5cf6';
    drawAlarmSounderIcon(ctx, preview.position, size);
  } else if (preview.type === 'wc-call-point') {
    ctx.fillStyle = '#3b82f6'; // Blue-500
    ctx.strokeStyle = '#3b82f6';
    drawWCCallPointIcon(ctx, preview.position, size);
  } else if (preview.type === 'ac-unit') {
    ctx.fillStyle = '#06b6d4'; // Cyan-500
    ctx.strokeStyle = '#06b6d4';
    drawACUnitIcon(ctx, preview.position, size);
  } else if (preview.type === 'fire-alarm') {
    ctx.fillStyle = '#ef4444'; // Red-500
    ctx.strokeStyle = '#ef4444';
    drawFireAlarmIcon(ctx, preview.position, size);
  } else if (preview.type === 'ice-machine') {
    ctx.fillStyle = '#0ea5e9'; // Sky-500
    ctx.strokeStyle = '#0ea5e9';
    drawIceMachineIcon(ctx, preview.position, size);
  } else if (preview.type === 'coffee-machine') {
    ctx.fillStyle = '#92400e'; // Brown-800
    ctx.strokeStyle = '#92400e';
    drawCoffeeMachineIcon(ctx, preview.position, size);
  }

  ctx.restore();
}
